import { Component, ElementRef, forwardRef, Input, Output, EventEmitter, OnInit, ViewChild, Renderer2, ChangeDetectorRef, AfterContentChecked } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { ClipboardService } from 'ngx-clipboard';

type InputType = 'text' | 'password' | 'email' | 'username';
type InputSize = 'small' | 'medium';
type InputBorder = 'bottom' | 'full';

const iconClasses = {
  passwordVisible: 'icon-smash_open_eye',
  passwordHidden: 'icon-smash_close_eye',
  copy: "icon-smash_copy",
  tooltip: "icon-smash_informations",
};

const defaultInputType = 'text';
const defaultInputSize = 'medium';
const defaultBorderStyle = 'bottom';

@Component({
  selector: 'app-smash-input',
  templateUrl: './smash-input.component.html',
  styleUrls: ['./smash-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SmashInputComponent),
      multi: true
    },
  ]
})
export class SmashInputComponent implements OnInit, AfterContentChecked, ControlValueAccessor {

  @Input() type: InputType = defaultInputType;
  @Input() size: InputSize = defaultInputSize;
  @Input() borderStyle: InputBorder = defaultBorderStyle;
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() processing = false;
  @Input() autocapitalize = 'none';
  @Input() formControlName = '';

  /* Handle error style */
  @Input() error: boolean;
  /* Used for activate or no browser prefill */
  @Input() autocomplete = 'off';
  /* Prefix content */
  @Input() title: string;
  /* Prefix content */
  @Input() prefix: string;
  /* Suffix content */
  @Input() suffix: string;
  /* Max caracter length for input */
  @Input() maxLength: number;
  /* Enable copy content */
  @Input() copyEnabled = false;
  /* Tooltip */
  @Input() tooltipContent: string;

  /* Maybe to remove */
  @Input() max: number;
  @Input('value') _value: any;

  /* Event on value change */
  @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();
  /* Event went value is copied */
  @Output() copy: EventEmitter<any> = new EventEmitter<any>();
  /* Event on native input focus */
  @Output() inputFocus: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
  /* Event on native input blur */
  @Output() inputBlur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

  @ViewChild('input') input: ElementRef;


  /* Border classes for input */
  inputBorderClasses = {
    full: 'border-full',
    bottom: 'border-bottom',
  }

  /* Input is focused or not */
  inputFocused = false;

  iconType: string;
  iconClass: string;

  passwordVisible = false;
  tooltipVisible = false;

  defaultTooltipDirection = 'right';

  constructor(private renderer: Renderer2, private clipboardService: ClipboardService, private ref: ChangeDetectorRef) { }

  ngOnInit(): void {
    if (this.type === 'password') {
      this.iconType = 'password';
      this.iconClass = iconClasses.passwordHidden;
    }
    if (this.tooltipContent) {
      this.iconClass = iconClasses.tooltip;
    }
    if (this.copyEnabled) {
      this.iconType = 'copy';
      this.iconClass = iconClasses.copy;
    }
  }

  ngAfterContentChecked(): void {
    this.ref.detectChanges();
  }

  togglePassword() {
    if (!this.disabled) {
      this.passwordVisible = !this.passwordVisible;
      if (this.passwordVisible) {
        this.iconClass = iconClasses.passwordVisible;
        this.renderer.setAttribute(this.input.nativeElement, "type", "text");
      } else {
        this.iconClass = iconClasses.passwordHidden;
        this.renderer.setAttribute(this.input.nativeElement, "type", "password");
      }
    }
  }

  toggleCopy() {
    if (this.value) {
      this.clipboardService.copyFromContent(this.value);
      this.copy.emit(this.value);
    }
  }

  toggleIcon() {
    switch (this.iconType) {
      case 'password':
        this.togglePassword();
        break;
      case 'copy':
        this.toggleCopy();
        break;
    }
  }

  handleMainClick() {
    switch (this.iconType) {
      case 'copy':
        this.toggleCopy();
        break;
    }
  }

  /* Called when input is focused */
  onInputFocus(event: FocusEvent) {
    this.inputFocused = true;
    this.inputFocus.emit(event);
  }

  /* Called when input is out focused */
  onInputFocusOut(event: FocusEvent) {
    this.inputFocused = false;
    this.inputBlur.emit(event);
  }

  set value(val) {
    this._value = val;
    this.onChange(val);
    this.onTouched();
    this.valueChange.emit(val);
  }

  /* ControlValueAccessor specifications */
  onChange: any = () => { };
  onTouched: any = () => { };
  validateFn: any = () => { };

  get value() {
    return this._value;
  }

  // The FORM-input path: assign the backing field only, never notify the form back. Routing this through
  // the `value` setter called onChange/onTouched, so every programmatic write became a fake user edit --
  // `setValue(..., { emitEvent: false })` emitted anyway and the control was marked dirty/touched by a
  // write the user never made. The falsy branch also turned a legitimate empty string into null, changing
  // its type behind the form back. See cva-contract.spec.ts.
  writeValue(value: any) {
    this._value = value ?? null;
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  handleChange(event: any) {
    if (this.type === 'email') {
      this.value = event.target.value.toLowerCase();
    }
  }

  focus() {
    this.input.nativeElement.focus();
  }
}
